import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, useScroll, useSpring } from "framer-motion";
import { FiCalendar, FiClock, FiArrowLeft } from "react-icons/fi";
import { API_BASE_URL } from "../utils/constant";
import ConnectUs from "../Components/ContactForm/ContactUs";

// --- Helper Functions (No changes needed) ---
const processContent = (htmlContent) => {
    if (!htmlContent) return { sanitizedHtml: "", headings: [] };
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const headings = [];
    doc.body.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((node, index) => {
        const text = node.textContent || "";
        const id = `heading-${index}-${text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '')}`;
        node.setAttribute('id', id);
        headings.push({ id, text, level: parseInt(node.tagName.substring(1), 10) });
    });
    return { sanitizedHtml: doc.body.innerHTML, headings };
};
const calculateReadingTime = (htmlContent) => {
    if (!htmlContent) return 0;
    const text = htmlContent.replace(/<[^>]+>/g, '');
    const wordsPerMinute = 225;
    const wordCount = text.split(/\s+/).length;
    return Math.ceil(wordCount / wordsPerMinute);
};
const createExcerpt = (html, wordLimit) => {
    if (!html) return "";
    const text = html.replace(/<[^>]+>/g, '');
    return text.split(/\s+/).slice(0, wordLimit).join(" ") + "...";
};

// --- ReadingProgressBar (Unchanged) ---
const ReadingProgressBar = () => {
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });
    return <motion.div className="fixed top-0 left-0 right-0 h-1 bg-blue-600 origin-left z-50" style={{ scaleX }} />;
};

// --- NEW: Minimalist Table of Contents ---
const TableOfContents = ({ headings }) => {
    const [activeId, setActiveId] = useState('');
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => entries.forEach(entry => {
                if (entry.isIntersecting) setActiveId(entry.target.id);
            }), { rootMargin: "0px 0px -80% 0px" }
        );
        headings.forEach(h => {
            const el = document.getElementById(h.id);
            if (el) observer.observe(el);
        });
        return () => headings.forEach(h => {
            const el = document.getElementById(h.id);
            if (el) observer.unobserve(el);
        });
    }, [headings]);

    if (headings.length < 2) return null;

    return (
        <nav>
            <h3 className="text-xs font-bold text-slate-800 mb-3 uppercase tracking-widest">On this page</h3>
            <ul className="space-y-2">
                {headings.map(h => (
                    <li key={h.id}>
                        <a href={`#${h.id}`}
                           className={`transition-colors duration-200 text-sm ${activeId === h.id ? 'text-blue-600 font-bold' : 'text-slate-500 hover:text-slate-900'}`}
                           style={{ paddingLeft: `${(h.level - 1) * 1}rem` }}>
                            {h.text}
                        </a>
                    </li>
                ))}
            </ul>
        </nav>
    );
};

// --- NEW: Clean Metadata Component ---
const ArticleMetadata = ({ blog }) => (
    <div className="flex items-center justify-center gap-x-6 gap-y-2 mt-6 text-sm text-slate-600 flex-wrap">
        <div className="flex items-center gap-2">
            <img src={blog.author_image_url} alt={blog.author_name} className="w-8 h-8 rounded-full object-cover"/>
            <span className="font-semibold text-slate-800">{blog.author_name}</span>
        </div>
        <span className="text-slate-400 hidden sm:inline">|</span>
        <div className="flex items-center gap-2">
            <FiCalendar className="text-slate-400"/>
            <span>{blog.published_at}</span>
        </div>
        <span className="text-slate-400 hidden sm:inline">|</span>
        <div className="flex items-center gap-2">
            <FiClock className="text-slate-400"/>
            <span>{blog.readingTime} min read</span>
        </div>
    </div>
);

// --- REVAMPED: Skeleton Component ---
const BlogDetailSkeleton = () => (
    <div className="bg-white min-h-screen animate-pulse">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
            <div className="lg:col-span-12 h-96 bg-slate-200 rounded-2xl mb-12"></div>
            <div className="max-w-3xl mx-auto text-center">
                <div className="h-4 bg-slate-200 rounded-full w-24 mx-auto mb-4"></div>
                <div className="h-12 bg-slate-300 rounded-md w-full mb-4"></div>
                <div className="h-10 bg-slate-300 rounded-md w-3/4 mx-auto mb-6"></div>
                <div className="h-6 bg-slate-200 rounded w-1/2 mx-auto"></div>
            </div>
            <div className="max-w-3xl mx-auto mt-16 space-y-6">
                <div className="h-6 bg-slate-200 rounded w-full"></div>
                <div className="h-6 bg-slate-200 rounded w-5/6"></div>
            </div>
        </div>
    </div>
);

// --- Main BlogDetails Component ---
function BlogDetails() {
    const { slug } = useParams();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [headings, setHeadings] = useState([]);

    useEffect(() => {
        const fetchBlog = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/blogs/${slug}`);
                if (!response.ok) throw new Error("Blog post not found or failed to load.");
                const data = await response.json();
                const { sanitizedHtml, headings: extractedHeadings } = processContent(data.content);
                setBlog({
                    ...data, content: sanitizedHtml, readingTime: calculateReadingTime(sanitizedHtml), author_name: data.author_name || "Alsol Team", author_image_url: data.author_image_url || 'default_avatar.png', published_at: new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(new Date(data.published_at)), category: data.categories?.[0]?.name || "Insights"
                });
                setHeadings(extractedHeadings);
            } catch (err) { setError(err.message); } finally { setLoading(false); }
        };
        fetchBlog();
        window.scrollTo(0, 0);
    }, [slug]);

    if (loading) return <BlogDetailSkeleton />;
    if (error) return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center py-20 bg-white">
            <h2 className="text-3xl font-bold text-slate-800">Oops! Something went wrong.</h2>
            <Link to="/blog" className="inline-flex items-center mt-8 bg-blue-600 text-white font-semibold py-2.5 px-6 rounded-lg shadow-sm hover:bg-blue-700 transition-colors">
                <FiArrowLeft className="mr-2 h-4 w-4" /> Go Back to Blog
            </Link>
        </div>
    );
    if (!blog) return null;

    return (
        <>
            <ReadingProgressBar />
            <Helmet>
                <title>{blog.meta_title || blog.title}</title>
                <meta name="description" content={blog.meta_description || createExcerpt(blog.content, 25)} />
            </Helmet>
            
            <main className="bg-white pt-16 sm:pt-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Link to="/blog" className="inline-flex items-center text-sm text-blue-600 font-semibold hover:underline mb-8">
                        <FiArrowLeft className="mr-2"/> Back to all articles
                    </Link>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-x-12">
                        
                        {/* --- Main Content Area (Spans entire width on top, then shifts) --- */}
                        <div className="lg:col-span-8 lg:col-start-3">
                            
                            {/* Featured Image */}
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                               <img src={blog.featured_image_url} alt={`Featured for ${blog.title}`}
                                 className="w-full h-full object-fit rounded-2xl shadow-xl aspect-[16/9] mb-8 sm:mb-12" />
                            </motion.div>

                            {/* Article Header */}
                            <header className="text-center">
                                <motion.p className="text-base font-semibold text-blue-600 uppercase tracking-wider"
                                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.5 }}>
                                    {blog.category}
                                </motion.p>
                                <motion.h1 className="mt-2 text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight"
                                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.5 }}>
                                    {blog.title}
                                </motion.h1>
                                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.5 }}>
                                    <ArticleMetadata blog={blog} />
                                </motion.div>
                            </header>

                             <hr className="my-12 border-slate-200" />
                            
                            {/* Article Body */}
                            <motion.article
                                className="prose prose-lg max-w-none prose-slate prose-headings:font-bold prose-headings:text-slate-800 prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline prose-img:rounded-xl prose-img:shadow-md"
                                dangerouslySetInnerHTML={{ __html: blog.content }}
                                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.7 }}
                            />
                        </div>
                        
                        {/* --- Sticky Table of Contents on the right --- */}
                        <aside className="hidden lg:block lg:col-span-2 lg:col-start-11">
                            <div className="sticky top-28">
                                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6, duration: 0.5 }}>
                                    <TableOfContents headings={headings} />
                                </motion.div>
                            </div>
                        </aside>
                    </div>
                </div>
            </main>
            
            <div className="bg-white pt-20">
              <ConnectUs />
            </div>
        </>
    );
}

export default BlogDetails;